import { useState } from "react";

import { MAX_DURATION_S } from "@/data/alarmRules/engineConstants";
import { cn } from "@/lib/cn";
import { NumberField } from "@/components/ui/NumberField";

type Unit = "s" | "m" | "h";

interface Props {
  /** Total duration in seconds. */
  value: number;
  onChange: (seconds: number) => void;
  max?: number; // override `MAX_DURATION_S` (e.g. windows allow 30 d)
  disabled?: boolean;
  ariaLabel?: string;
}

function pickInitialUnit(seconds: number): Unit {
  if (seconds === 0) return "s";
  if (seconds % 3600 === 0) return "h";
  if (seconds % 60 === 0) return "m";
  return "s";
}

function toSeconds(amount: number, unit: Unit): number {
  if (unit === "h") return amount * 3600;
  if (unit === "m") return amount * 60;
  return amount;
}

function fromSeconds(seconds: number, unit: Unit): number {
  if (unit === "h") return seconds / 3600;
  if (unit === "m") return seconds / 60;
  return seconds;
}

export function DurationInput({
  value,
  onChange,
  max = MAX_DURATION_S,
  disabled,
  ariaLabel,
}: Props) {
  const [unit, setUnit] = useState<Unit>(() => pickInitialUnit(value));
  const amount = fromSeconds(value, unit);

  return (
    <div className="flex items-center gap-1">
      <NumberField
        value={Number.isFinite(amount) ? amount : 0}
        onChange={(raw) => {
          const secs = Math.min(max, Math.round(toSeconds(Math.max(0, raw), unit)));
          onChange(secs);
        }}
        min={0}
        step={unit === "s" ? 1 : unit === "m" ? 0.5 : 0.1}
        disabled={disabled}
        aria-label={ariaLabel ?? "Duration"}
        className="form-input w-24"
      />
      <select
        value={unit}
        disabled={disabled}
        onChange={(e) => setUnit(e.target.value as Unit)}
        className={cn("form-input w-16")}
        aria-label="Duration unit"
      >
        <option value="s">s</option>
        <option value="m">min</option>
        <option value="h">h</option>
      </select>
    </div>
  );
}
